import { GeneralCandidate } from '../../../store/types/models/GeneralCandidate'
import { generatePath, useNavigate } from 'react-router-dom'
import navigation from '../../../config/navigation'
import React, { useEffect, useRef, useState } from 'react'
import { Avatar, Box, Card, CardActions, CardContent, CardHeader, Chip, Divider, Typography } from '@mui/material'
import { FmdGoodOutlined, SignalCellularAlt } from '@mui/icons-material'
import AvatarComponent from '../../../components/avatar/AvatarComponent'
import { UsersTechnology } from '../../../store/types'
import { getImageSrc } from '../../../store/utils'
import dayjs from 'dayjs'
import DateFormat from '../../../store/types/enums/DateFormat'
import CandidateTechnologyTooltip from './CandidateTechnologyTooltip'
import { t } from 'i18next'
import LikeDislikeCommentSection from '../../../components/LikeDislike/LikeDislikeCommentSection'
import TargetType from '../../../store/types/enums/TargetType'
import DecisionEnum from '../../../store/types/enums/DecisionEnum'
import { MAX_TECHNOLOGY_ROW_WIDTH } from '../../../store/constants/technologyCard'

type CandidateCardProps = {
  candidate: GeneralCandidate
  setCommentSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>
  setCommentCandidate: React.Dispatch<React.SetStateAction<GeneralCandidate | undefined>>
  isDragged: boolean
}

const WAITING_FOR_REPLENISHMENT = 'Oczekiwanie na uzupełnienie'

const CandidateCard = ({ candidate, setCommentSidebarOpen, setCommentCandidate }: CandidateCardProps) => {
  const navigate = useNavigate()
  const candidateDetailsPath = generatePath(navigation.candidateDetails, { id: candidate.id })
  const [expanded, setExpanded] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState<number>(candidate.technologies.length)
  const visibleTechnologies = expanded ? candidate.technologies : candidate.technologies.slice(0, visibleCount + 1)
  const hasHiddenTechnologies = candidate.technologies.length > visibleCount + 1

  useEffect(() => {
    if (!containerRef.current) return

    const chipElements = Array.from(containerRef.current.children) as HTMLDivElement[]
    let totalWidth = 0
    let count = 0

    for (let chip of chipElements) {
      const chipWidth = chip.getBoundingClientRect().width + 4
      if (totalWidth + chipWidth <= MAX_TECHNOLOGY_ROW_WIDTH) {
        totalWidth += chipWidth
        count++
      } else {
        break
      }
    }

    setVisibleCount(count)
  }, [candidate.technologies])

  const handleCommentClick = () => {
    setCommentSidebarOpen(true)
    setCommentCandidate(candidate)
  }

  const decision = candidate.recruitment.decision

  const decisionBorderColors: Partial<Record<DecisionEnum, string>> = {
    [DecisionEnum.ACCEPTED]: 'accent.green.main',
    [DecisionEnum.REJECTED]: 'warning.main',
  }

  const borderColor = decisionBorderColors[decision]

  return (
    <Card
      elevation={4}
      sx={{
        ...(borderColor && {
          borderBottom: '2px solid',
          borderColor,
        }),
        cursor: 'grab',
      }}
    >
      <CardHeader
        sx={{ pb: 1 }}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography
                component={'h5'}
                variant={'h6'}
                onClick={() => navigate(candidateDetailsPath)}
                sx={{
                  lineHeight: 1.2,
                  fontSize: '16px',
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >{`${candidate.firstName} ${candidate.lastName}`}</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 }}>
                <FmdGoodOutlined sx={{ fontSize: 18 }} />
                <Typography variant={'subtitle2'}>{candidate.localization}</Typography>
              </Box>
            </Box>
            <AvatarComponent
              avatar={undefined}
              extension={undefined}
              alt={candidate.firstName + ' ' + candidate.lastName}
              size={36}
              candidate
            />
          </Box>
        }
      />
      <CardContent sx={{ pt: 0, pb: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Invisible measurement row */}
          <Box
            ref={containerRef}
            sx={{
              position: 'absolute',
              visibility: 'hidden',
              height: 0,
              overflow: 'hidden',
              display: 'flex',
              gap: 0.5,
            }}
          >
            {candidate.technologies.map(tech => (
              <Chip
                key={tech.technologyId}
                size="small"
                label={tech.name}
                avatar={
                  <Avatar
                    src={getImageSrc(tech.logo, tech.extension)}
                    alt={tech.name}
                    sx={{ backgroundColor: 'background.elevation8' }}
                  />
                }
                variant="outlined"
              />
            ))}
          </Box>

          {/* Actual visible chips */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {visibleTechnologies.map((tech: UsersTechnology) => (
              <CandidateTechnologyTooltip key={tech.technologyId} technology={tech}>
                <Chip
                  size="small"
                  avatar={
                    <Avatar
                      src={getImageSrc(tech.logo, tech.extension)}
                      alt={tech.name}
                      sx={{ backgroundColor: 'background.elevation8' }}
                    />
                  }
                  label={tech.name}
                  variant="outlined"
                />
              </CandidateTechnologyTooltip>
            ))}
            {hasHiddenTechnologies && !expanded && (
              <Chip label="..." size="small" variant="outlined" onClick={() => setExpanded(true)} />
            )}
            {hasHiddenTechnologies && expanded && (
              <Chip
                label={t('page.candidates.showLess')}
                size="small"
                variant="outlined"
                onClick={() => setExpanded(false)}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box
              sx={{
                display: 'flex',
                width: candidate.recruitment.step !== WAITING_FOR_REPLENISHMENT ? '65%' : '100%',
              }}
            >
              <SignalCellularAlt sx={{ width: '0.9em', height: '0.9em' }} />
              <Typography variant={'subtitle2'} fontWeight={450} alignSelf={'center'}>
                {candidate.recruitment.step}
              </Typography>
            </Box>
            {candidate.recruitment.step !== WAITING_FOR_REPLENISHMENT && (
              <Typography variant={'subtitle2'}>
                {dayjs(candidate.recruitment.completionDate).format(DateFormat.DayMonthYear)}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
      <Divider />
      <CardActions sx={{ px: 2 }}>
        <LikeDislikeCommentSection
          handleCommentClick={handleCommentClick}
          targetId={candidate.id}
          targetType={TargetType.CANDIDATE}
          upVoters={candidate.upVoters}
          downVoters={candidate.downVoters}
          commentsCount={candidate.commentsCount}
          isFollowingCandidate={candidate.isFollowing}
        />
      </CardActions>
    </Card>
  )
}

export default React.memo(CandidateCard)
